import { z } from 'zod';

/** TI, Acessos (backlog A1, A2, A7): sistemas e concessões de acesso dos colaboradores. */

export const IT_SYSTEM_KINDS = [
  'directory',
  'email',
  'saas',
  'repository',
  'erp',
  'infrastructure',
  'other',
] as const;
export const IT_SYSTEM_CRITICALITIES = ['low', 'medium', 'high'] as const;
export const IT_ACCESS_LEVELS = ['user', 'admin', 'privileged', 'local_admin'] as const;
export const IT_ACCESS_STATUSES = [
  'requested',
  'approved',
  'granted',
  'rejected',
  'revoked',
  'expired',
] as const;
/** Níveis que exigem data de validade e vencem sozinhos (A7). */
export const IT_TEMPORARY_LEVELS = ['privileged', 'local_admin'] as const;

const id = z.uuid().nullable().optional();
const day = z.iso.date();

export const itSystemInput = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Z0-9][A-Z0-9_.-]{1,29}$/, 'Use de 2 a 30 letras maiúsculas, números, ., - ou _'),
  name: z.string().trim().min(2).max(200),
  legalEntityId: id,
  kind: z.enum(IT_SYSTEM_KINDS),
  softwareId: id,
  technicalOwnerUserId: id,
  businessOwnerUserId: id,
  criticality: z.enum(IT_SYSTEM_CRITICALITIES).default('medium'),
  mfaRequired: z.boolean().default(false),
  mfaEnabled: z.boolean().default(false),
  isActive: z.boolean().default(true),
  notes: z.string().trim().max(2000).optional(),
  // Repositórios e sistemas (G8, 0342): ambiente e se a conta é da empresa (titularidade).
  environment: z.enum(['production', 'development', 'both']).nullable().optional(),
  companyOwned: z.boolean().nullable().optional(),
});
export type ItSystemInput = z.infer<typeof itSystemInput>;

export const itAccessRequestInput = z
  .object({
    systemId: z.uuid(),
    legalEntityId: z.uuid(),
    employeeId: z.uuid(),
    level: z.enum(IT_ACCESS_LEVELS),
    justification: z.string().trim().min(3, 'Explique a necessidade').max(2000),
    costCenterId: z.uuid(),
    validUntil: day.nullable().optional(),
  })
  .refine((v) => !(IT_TEMPORARY_LEVELS as readonly string[]).includes(v.level) || !!v.validUntil, {
    path: ['validUntil'],
    message: 'Acesso privilegiado ou administrador local precisa de validade',
  });
export type ItAccessRequestInput = z.infer<typeof itAccessRequestInput>;

export const itAccessRevokeInput = z.object({
  reason: z.string().trim().min(3, 'Explique o motivo').max(1000),
});

/** Revisão periódica dos acessos (A5, A6): a campanha de uma empresa. */
export const itAccessReviewInput = z.object({
  legalEntityId: z.uuid(),
  title: z.string().trim().min(3).max(200),
  dueOn: day,
});
export type ItAccessReviewInput = z.infer<typeof itAccessReviewInput>;

export const itAccessReviewDecisionInput = z
  .object({
    decision: z.enum(['keep', 'revoke']),
    note: z.string().trim().max(1000).optional(),
  })
  .refine((v) => v.decision !== 'revoke' || (v.note ?? '').length >= 3, {
    path: ['note'],
    message: 'Explique por que o acesso deve ser retirado',
  });

/** Política da intranet exigida para aceite (A8). */
export const itPolicyRequirementInput = z.object({
  postId: z.uuid(),
  isActive: z.boolean().default(true),
});

/** Revisão periódica do software (A9): a próxima data e o que foi conferido. */
export const itSoftwareReviewInput = z.object({
  nextReviewOn: day,
  note: z.string().trim().min(3, 'Conte o que foi conferido').max(1000),
});
